import React from 'react';

export default function TermsPage() {
  const sectionHeading = { color: '#1e293b', fontSize: '1.15rem', fontWeight: '600', marginTop: '1.75rem', marginBottom: '0.5rem' };
  const bodyText = { color: '#475569', fontSize: '0.9rem', lineHeight: '1.7' };
  const bulletList = { color: '#475569', fontSize: '0.9rem', lineHeight: '1.7', paddingLeft: '1.25rem' };
  const capsText = { color: '#475569', fontSize: '0.9rem', lineHeight: '1.7', fontWeight: '700', textTransform: 'uppercase' };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '2rem',
    }}>
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        padding: '2.5rem',
        width: '100%',
        maxWidth: '760px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
        marginTop: '1rem',
        marginBottom: '2rem',
      }}>
        <a href="/" style={{ color: '#01696F', fontSize: '0.85rem', textDecoration: 'none' }}>&larr; Back to home</a>

        <div style={{
          backgroundColor: '#fef9c3',
          border: '1px solid #facc15',
          borderRadius: '8px',
          padding: '0.75rem 1rem',
          marginTop: '1.25rem',
          marginBottom: '1.25rem',
          fontSize: '0.85rem',
          fontWeight: '600',
          color: '#854d0e',
          textAlign: 'center',
        }}>
          DRAFT — Pending external counsel review of arbitration, AADC, and governing-law clauses
        </div>

        <h1 style={{ color: '#1e293b', fontSize: '1.5rem', fontWeight: '700', marginTop: '1rem', marginBottom: '0.25rem' }}>Terms of Service</h1>
        <p style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '2rem' }}>Effective Date: April 26, 2026</p>

        {/* 1. Agreement to Terms */}
        <h2 style={sectionHeading}>1. Agreement to Terms</h2>
        <p style={bodyText}>
          By creating an account or using BragSheet, you agree to these Terms of Service and our Privacy Policy. If you do not agree, do not use the service. These Terms constitute a legally binding agreement between you and Bragsheet LLC ("BragSheet," "we," "us," or "our").
        </p>

        {/* 2. Eligibility and Age Requirements */}
        <h2 style={sectionHeading}>2. Eligibility and Age Requirements</h2>
        <p style={bodyText}>
          You must be at least 13 years old to use BragSheet.
        </p>
        <p style={bodyText}>
          If you are between 13 and 17 years old, you represent that your parent or legal guardian has reviewed these Terms (including the arbitration and class-action waiver in Section 14), consents to your use of the service, and personally agrees to be bound by these Terms — including the arbitration and class-action waiver — both on your behalf and on their own behalf. By allowing a minor to use BragSheet, the parent or guardian additionally accepts responsibility for the minor's activity on the service. Nothing in these Terms is intended to waive any non-waivable rights of a minor under applicable law.
        </p>

        {/* 3. Description of Service */}
        <h2 style={sectionHeading}>3. Description of Service</h2>
        <p style={{ ...bodyText, marginBottom: '0.5rem' }}>
          BragSheet is a journaling and college preparation tool for high school students. The service includes:
        </p>
        <ul style={bulletList}>
          <li>Journal entries with categories and tags</li>
          <li>Voice input for journal entries</li>
          <li>Profile management (GPA, test scores, activities, awards)</li>
          <li>AI-powered suggestions (essay themes, auto-tags, activity descriptions)</li>
          <li>Data export (JSON, CSV)</li>
        </ul>

        {/* 4. User Accounts */}
        <h2 style={sectionHeading}>4. User Accounts</h2>
        <ul style={bulletList}>
          <li>You must provide accurate and complete information when creating an account.</li>
          <li>You are responsible for maintaining the security of your account credentials.</li>
          <li>You must notify us immediately at <a href="mailto:support@bragsheet.io" style={{ color: '#01696F' }}>support@bragsheet.io</a> of any unauthorized use of your account.</li>
          <li>One account per person. Do not share your account credentials with others.</li>
          <li>We may suspend or terminate accounts that violate these Terms.</li>
        </ul>

        {/* 5. Acceptable Use */}
        <h2 style={sectionHeading}>5. Acceptable Use</h2>
        <p style={{ ...bodyText, marginBottom: '0.5rem' }}>You agree not to:</p>
        <ul style={bulletList}>
          <li>Use the service for any unlawful purpose</li>
          <li>Attempt to gain unauthorized access to the service or its infrastructure</li>
          <li>Upload malicious content, viruses, or code intended to disrupt the service</li>
          <li>Use automated tools (bots, scrapers) to access the service without our permission</li>
          <li>Impersonate another person or misrepresent your identity</li>
          <li>Use the service to harass, abuse, or harm others</li>
          <li>Circumvent any security measures or rate limits</li>
          <li>Use the service for commercial purposes without our written permission</li>
        </ul>

        {/* 6. Your Content */}
        <h2 style={sectionHeading}>6. Your Content</h2>
        <ul style={bulletList}>
          <li>You retain full ownership of all content you create on BragSheet, including journal entries, profile data, activities, and awards.</li>
          <li>You grant BragSheet a limited, non-exclusive license to store, process, display, and transmit your content solely to provide and improve the service.</li>
          <li>This license terminates when you delete your content or your account.</li>
          <li>We do not claim ownership of your content and will not use it for advertising or marketing without your explicit consent.</li>
        </ul>

        {/* 7. AI-Generated Content and Academic Integrity */}
        <h2 style={sectionHeading}>7. AI-Generated Content and Academic Integrity</h2>
        <p style={{ ...bodyText, marginBottom: '0.5rem' }}>
          BragSheet provides AI-powered suggestions including essay themes, auto-tags, and activity descriptions. Regarding AI-generated content:
        </p>
        <ul style={bulletList}>
          <li>AI suggestions are generated by artificial intelligence and are provided as a starting point for your own work.</li>
          <li>We do not guarantee the accuracy, completeness, originality, or suitability of AI-generated content.</li>
          <li>AI-generated content may be similar or identical to suggestions provided to other users.</li>
          <li>AI output may not be protectable under intellectual property law.</li>
          <li><strong>You are solely responsible for ensuring that any content you submit in college applications, scholarship applications, or academic contexts is your own original work and complies with the integrity policies of the institutions to which you are applying.</strong></li>
          <li>BragSheet is not responsible for any consequences arising from your use of AI-generated suggestions in applications, including but not limited to rejection, rescission of admission, or disciplinary action.</li>
          <li>We recommend using AI suggestions as inspiration and a framework, not as final copy.</li>
        </ul>

        {/* 8. BragSheet Intellectual Property */}
        <h2 style={sectionHeading}>8. BragSheet Intellectual Property</h2>
        <p style={bodyText}>
          BragSheet and its licensors retain all rights, title, and interest in the service, including all software, designs, text, graphics, logos, and trademarks. The BragSheet name, logo, and all related marks are trademarks of Bragsheet LLC. You may not use our trademarks, service marks, or trade dress without prior written permission.
        </p>

        {/* 9. Service Availability and Modifications */}
        <h2 style={sectionHeading}>9. Service Availability and Modifications</h2>
        <ul style={bulletList}>
          <li>We strive to keep BragSheet available but do not guarantee uninterrupted or error-free service.</li>
          <li>We may modify, suspend, or discontinue any part of the service with reasonable notice (at least 30 days for material changes).</li>
          <li>We are not liable for any modification, suspension, or discontinuation of the service.</li>
          <li>We strongly recommend that you regularly export your data using the built-in export features.</li>
        </ul>

        {/* 10. Termination */}
        <h2 style={sectionHeading}>10. Termination</h2>
        <p style={{ ...bodyText, marginBottom: '0.5rem' }}>
          <strong>By you:</strong> You may delete your account at any time through the Profile page (Danger Zone section). Upon deletion, all your data will be permanently removed within 30 days.
        </p>
        <p style={{ ...bodyText, marginBottom: '0.5rem' }}>
          <strong>By us:</strong> We may suspend or terminate your account: (a) immediately if you violate these Terms, engage in fraud, or misuse the service; (b) with 30 days notice for any other reason. Upon termination by us, you will have 30 days to export your data before it is deleted.
        </p>
        <p style={bodyText}>
          <strong>Effect of termination:</strong> Your right to use the service ceases immediately upon termination. Sections 7 (Academic Integrity), 8 (IP), 11 (Warranty), 12 (Liability), 13 (Indemnification), 14 (Dispute Resolution), and 15 (Governing Law) survive termination.
        </p>

        {/* 11. Disclaimer of Warranties — ALL CAPS + BOLD for legal conspicuousness */}
        <h2 style={sectionHeading}>11. Disclaimer of Warranties</h2>
        <p style={capsText}>
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING BUT NOT LIMITED TO THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE, THAT DEFECTS WILL BE CORRECTED, OR THAT AI-GENERATED CONTENT WILL BE ACCURATE, COMPLETE, OR SUITABLE FOR ANY PARTICULAR PURPOSE.
        </p>

        {/* 12. Limitation of Liability — ALL CAPS + BOLD for legal conspicuousness */}
        <h2 style={sectionHeading}>12. Limitation of Liability</h2>
        <p style={{ ...capsText, marginBottom: '0.75rem' }}>
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL BRAGSHEET LLC, ITS OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF DATA, LOSS OF PROFITS, OR LOSS OF GOODWILL, ARISING FROM OR RELATED TO YOUR USE OF THE SERVICE.
        </p>
        <p style={capsText}>
          BRAGSHEET'S TOTAL AGGREGATE LIABILITY TO YOU FOR ALL CLAIMS ARISING FROM OR RELATED TO THESE TERMS OR THE SERVICE SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNTS YOU HAVE PAID TO BRAGSHEET IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED DOLLARS ($100).
        </p>

        {/* 13. Indemnification */}
        <h2 style={sectionHeading}>13. Indemnification</h2>
        <p style={bodyText}>
          You agree to indemnify, defend, and hold harmless Bragsheet LLC and its officers, directors, employees, and agents from and against any claims, damages, losses, liabilities, and expenses (including reasonable attorneys' fees) arising from or related to: (a) your use of the service, (b) your content, (c) your violation of these Terms, (d) your violation of any third-party rights, or (e) your use of AI-generated content in college applications or other submissions.
        </p>

        {/* 14. Dispute Resolution and Arbitration */}
        <h2 style={sectionHeading}>14. Dispute Resolution and Arbitration</h2>
        <p style={{ ...bodyText, marginBottom: '0.75rem' }}>
          Any dispute, claim, or controversy arising from or relating to these Terms or the service shall be resolved by binding individual arbitration administered by the American Arbitration Association (AAA) under its Consumer Arbitration Rules. Arbitration shall take place in the state where you reside (or remotely, at your election).
        </p>
        <p style={{ ...bodyText, marginBottom: '0.75rem' }}>
          <strong>Class Action Waiver:</strong> You and BragSheet agree to resolve disputes only on an individual basis and waive any right to participate in a class, consolidated, or representative action.
        </p>
        <p style={{ ...bodyText, marginBottom: '0.75rem' }}>
          <strong>Opt-Out:</strong> You may opt out of this arbitration agreement by sending written notice to <a href="mailto:legal@bragsheet.io" style={{ color: '#01696F' }}>legal@bragsheet.io</a> within 30 days of first accepting these Terms. Your notice must include your name, email, and a clear statement that you wish to opt out.
        </p>
        <p style={bodyText}>
          <strong>Exceptions:</strong> Either party may bring claims in small claims court if eligible. Either party may seek injunctive or other equitable relief in any court of competent jurisdiction to prevent the actual or threatened infringement of intellectual property rights.
        </p>

        {/* 15. Governing Law */}
        <h2 style={sectionHeading}>15. Governing Law</h2>
        <p style={bodyText}>
          These Terms are governed by the laws of the State of Tennessee, without regard to its conflict of law provisions. To the extent that arbitration does not apply, you consent to the exclusive jurisdiction of the state and federal courts located in Knox County, Tennessee.
        </p>

        {/* 16. General Provisions */}
        <h2 style={sectionHeading}>16. General Provisions</h2>
        <ul style={bulletList}>
          <li><strong>Severability:</strong> If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.</li>
          <li><strong>Entire Agreement:</strong> These Terms, together with the Privacy Policy, constitute the entire agreement between you and BragSheet regarding the service.</li>
          <li><strong>Assignment:</strong> You may not assign these Terms without our written consent. We may assign these Terms in connection with a merger, acquisition, or sale of all or substantially all of our assets, with notice to you.</li>
          <li><strong>Waiver:</strong> Our failure to enforce any provision of these Terms does not constitute a waiver of that provision.</li>
          <li><strong>Force Majeure:</strong> We are not liable for any failure to perform due to causes beyond our reasonable control, including natural disasters, war, terrorism, pandemics, strikes, government actions, or internet/infrastructure failures.</li>
          <li><strong>Notices:</strong> We may send notices to the email address associated with your account. You may send notices to <a href="mailto:legal@bragsheet.io" style={{ color: '#01696F' }}>legal@bragsheet.io</a>.</li>
        </ul>

        {/* 17. Contact */}
        <h2 style={sectionHeading}>17. Contact</h2>
        <p style={{ ...bodyText, marginBottom: '0.25rem' }}>For questions about these Terms:</p>
        <ul style={bulletList}>
          <li><strong>Email:</strong> <a href="mailto:legal@bragsheet.io" style={{ color: '#01696F' }}>legal@bragsheet.io</a></li>
          <li><strong>Mail:</strong> Bragsheet LLC, 116 Agnes Rd Ste 200, Knoxville, TN 37919, USA</li>
        </ul>
      </div>
    </div>
  );
}
